import React, { useState } from 'react';
import { OrderDetailsDTO, Receipt } from '../../types/order';
import { useAuth } from '../../context/AuthContext';
import ReceiptPrintView from './ReceiptPrintView';
import CreateReceiptModal from './CreateReceiptModal';

interface ReceiptsCardProps {
  order: OrderDetailsDTO;
  onReceiptCreated: () => Promise<void>;
}

const ReceiptsCard: React.FC<ReceiptsCardProps> = ({ order, onReceiptCreated }) => {
  const { user } = useAuth();
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const receipts = order.receipts || [];
  const invoiceTotal = order.price?.total || 0;
  const totalPaid = receipts.reduce((sum, r) => sum + r.amount, 0);
  const remainingBalance = Math.max(0, invoiceTotal - totalPaid);

  // Check if user can create receipts (admin or tailor)
  const canCreateReceipt = user?.role === 'admin' || user?.role === 'tailor';

  const formatCurrency = (amount: number) => {
    return `₹${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPaymentMethod = (method: string) => {
    const methodMap: Record<string, string> = {
      cash: 'Cash',
      credit_card: 'Credit Card',
      debit_card: 'Debit Card',
      upi: 'UPI',
      razorpay: 'Razorpay',
      stripe: 'Stripe',
      cheque: 'Cheque',
      bank_transfer: 'Bank Transfer',
      simulated: 'Simulated',
    };
    return methodMap[method.toLowerCase()] || method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handlePrintReceipt = (receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setShowPrintView(false);
        setSelectedReceipt(null);
      }, 500);
    }, 100);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Receipts</h3>
          {canCreateReceipt && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
            >
              + Create Receipt
            </button>
          )}
        </div>

        {/* Payment Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">Invoice Total</p>
              <p className="font-semibold text-gray-900">{formatCurrency(invoiceTotal)}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Total Paid</p>
              <p className="font-semibold text-green-600">{formatCurrency(totalPaid)}</p>
            </div>
            <div>
              <p className="text-gray-600 mb-1">Remaining</p>
              <p className={`font-semibold ${remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(remainingBalance)}
              </p>
            </div>
          </div>
        </div>

        {/* Receipts List */}
        {receipts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No receipts yet</p>
            {canCreateReceipt && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create First Receipt
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {receipts.map((receipt) => (
              <div
                key={receipt.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-900">{receipt.receiptNumber}</span>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {formatPaymentMethod(receipt.paymentMethod)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium text-gray-900">Amount:</span> {formatCurrency(receipt.amount)}
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Date:</span> {formatDate(receipt.paymentDate)}
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Remaining:</span>{' '}
                        <span className={receipt.remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatCurrency(receipt.remainingBalance)}
                        </span>
                      </div>
                      {receipt.transactionId && (
                        <div>
                          <span className="font-medium text-gray-900">Txn ID:</span>{' '}
                          <span className="font-mono text-xs">{receipt.transactionId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handlePrintReceipt(receipt)}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      Print
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Print View */}
      {showPrintView && selectedReceipt && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <ReceiptPrintView receipt={selectedReceipt} order={order} />
        </div>
      )}

      {/* Create Receipt Modal */}
      {showCreateModal && (
        <CreateReceiptModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          order={order}
          onReceiptCreated={onReceiptCreated}
        />
      )}
    </>
  );
};

export default ReceiptsCard;

